import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useCoachSearch } from "@/lib/coachQueries";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";

const TYPE_LABEL = {
  workspace: "Workspace", client: "Client", session: "Session", file: "File",
  feedback_request: "Feedback request", message_thread: "Message thread",
};

function ResultsList({ query, onNavigate }) {
  const navigate = useNavigate();
  const { data, isFetching } = useCoachSearch(query);
  const items = data?.items || [];

  function go(item) {
    onNavigate();
    navigate(item.deep_link);
  }

  if (query.trim().length < 2) return null;
  return (
    <div className="max-h-80 overflow-y-auto" data-testid="coach-global-search-results">
      {isFetching ? (
        <p className="px-3 py-2.5 text-sm text-ink-soft" data-testid="coach-global-search-loading">Searching…</p>
      ) : items.length === 0 ? (
        <p className="px-3 py-2.5 text-sm text-ink-soft" data-testid="coach-global-search-empty">No matches.</p>
      ) : (
        items.map((item, idx) => (
          <button
            key={`${item.type}-${idx}`}
            onClick={() => go(item)}
            className="w-full text-left px-3 py-2 hover:bg-ink/5 text-sm flex items-center justify-between gap-2"
            data-testid={`coach-global-search-result-${item.type}-${idx}`}
          >
            <span className="truncate">{item.label}</span>
            <span className="text-[10px] uppercase tracking-wide text-ink-soft shrink-0">{TYPE_LABEL[item.type] || item.type}</span>
          </button>
        ))
      )}
    </div>
  );
}

/**
 * Master v5 Prompt 16 — global, permission-filtered search. Every
 * result is pre-scoped server-side to Workspaces this coach actually
 * coaches (see coach_task_routes.py) — this component never sees, and
 * so can never leak, anything outside that set. Renders inline (with
 * a results flyout) on sm+ screens; on mobile it is an icon button
 * that opens a full-screen Dialog instead, so it never collides with
 * the topbar's other actions on narrow widths.
 */
export function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const blurTimer = useRef(null);

  return (
    <>
      <div className="relative hidden sm:block" data-testid="coach-global-search">
        <div className="flex items-center gap-2 border border-ink/15 rounded-lg px-3 py-2 bg-white/60">
          <Search size={16} className="text-ink-soft shrink-0" />
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
            placeholder="Search clients, sessions, files, feedback, messages…"
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-ink-soft/60"
            data-testid="coach-global-search-input"
          />
        </div>
        {open && (
          <div className="absolute left-0 right-0 mt-1 border border-ink/15 rounded-lg bg-white shadow-lg z-40" onMouseDown={() => clearTimeout(blurTimer.current)}>
            <ResultsList query={query} onNavigate={() => { setOpen(false); setQuery(""); }} />
          </div>
        )}
      </div>

      <button
        onClick={() => setMobileOpen(true)}
        className="sm:hidden p-2 rounded-full hover:bg-ink/5 text-ink-soft hover:text-ink transition-colors"
        aria-label="Search"
        data-testid="coach-global-search-mobile-trigger"
      >
        <Search size={18} />
      </button>
      <Dialog open={mobileOpen} onOpenChange={(v) => { setMobileOpen(v); if (!v) setQuery(""); }}>
        <DialogContent data-testid="coach-global-search-mobile-dialog">
          <DialogHeader><DialogTitle className="font-serif text-ink">Search</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 border border-ink/15 rounded-lg px-3 py-2">
            <Search size={16} className="text-ink-soft shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients, sessions, files…"
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-ink-soft/60"
              data-testid="coach-global-search-mobile-input"
            />
          </div>
          <ResultsList query={query} onNavigate={() => setMobileOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}

